import { useState, useMemo } from "react";
import { useTheme } from "../hooks/useTheme";
import { useQuery } from "@tanstack/react-query";
import Layout from "../layouts/LayoutWide.jsx";
import MetricChart from "../components/MetricChart.jsx";
import Spinner from "../components/Spinner.jsx";
import TimeRangePicker from "../components/TimeRangePicker.jsx";
import useTimezone from "../hooks/useTimezone.js";

const API = import.meta.env.VITE_API_BASE_URL;

const METRICS = [
  { key: "temperature", title: "Temperature", unit: "°F", color: "#f59e0b" },
  { key: "humidity",    title: "Humidity",    unit: "RH%",color: "#3b82f6" },
  { key: "co2",         title: "CO₂",         unit: "PPM",color: "#10b981" },
];

async function fetchDashboard() {
  const res = await fetch(`${API}dashboard`, { credentials: "include" });
  if (!res.ok) throw new Error(`dashboard ${res.status}`);
  return res.json();
}

function initialRange() {
  const end = Date.now();
  return { start: end - 3600_000, end };
}

export default function Analytics() {
  const [theme] = useTheme();
  const darkMode = theme === "dark";
  const tz = useTimezone();
  const [timeRange, setTimeRange] = useState(initialRange);
  const [selectedDevice, setSelectedDevice] = useState(null);

  const { data: dashboard, isLoading, isError } = useQuery({
    queryKey: ["dashboard"],
    queryFn: fetchDashboard,
    staleTime: 60_000,
  });

  // Build device list from dashboard response
  const devices = useMemo(() => {
    if (!dashboard?.devices) return [];
    return Object.values(dashboard.devices).map((d) => ({
      id: d.device.id,
      hostname: d.device.hostname,
      label: d.device.display_name || d.device.connectcode,
      sensors: d.sensors,
    }));
  }, [dashboard]);

  // Auto-select first device once loaded
  const activeHostname = useMemo(() => {
    if (selectedDevice) return selectedDevice;
    return devices[0]?.hostname ?? null;
  }, [selectedDevice, devices]);

  // Build a map of sensor_code → display label for the active device
  // Used by MetricChart to label series with friendly names instead of raw VM sensor codes
  const sensorLabels = useMemo(() => {
    const activeDevice = devices.find((d) => d.hostname === activeHostname);
    if (!activeDevice) return {};
    const map = {};
    for (const s of (activeDevice.sensors ?? [])) {
      map[s.sensor_code] = s.display_name || s.sensor_name || s.sensor_code;
    }
    return map;
  }, [devices, activeHostname]);


  if (isLoading) {
    return (
      <Layout title="Analytics">
        <div className="h-screen"><Spinner /></div>
      </Layout>
    );
  }

  if (isError || devices.length === 0) {
    return (
      <Layout title="Analytics">
        <div className="py-12 text-center text-gray-400">No devices found.</div>
      </Layout>
    );
  }

  return (
    <Layout title="Analytics">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Device selector */}
        {devices.length > 1 && (
          <select
            className="bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm rounded px-3 py-1.5 border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-indigo-500"
            value={activeHostname ?? ""}
            onChange={(e) => setSelectedDevice(e.target.value)}
          >
            {devices.map((d) => (
              <option key={d.hostname} value={d.hostname}>{d.label}</option>
            ))}
          </select>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-500 hidden sm:block">
            Scroll or pinch to zoom · Drag the slider below each chart
          </span>
          <TimeRangePicker
            start={timeRange.start}
            end={timeRange.end}
            onRangeChange={setTimeRange}
            dark
          />
        </div>
      </div>

      {/* Charts grid */}
      {activeHostname && (
        <div className="grid grid-cols-1 gap-6">
          {METRICS.map((m) => (
            <MetricChart
              key={m.key}
              title={m.title}
              metric={m.key}
              hostname={activeHostname}
              unit={m.unit}
              color={m.color}
              timeRange={timeRange}
              sensorLabels={sensorLabels}
              darkMode={darkMode}
              timeZone={tz}
            />
          ))}
        </div>
      )}
    </Layout>
  );
}
